/**
 * Pagination — simple prev/next with page indicator.
 *
 * Designed for cursor- or offset-paginated lists. Intentionally minimal.
 * Models/Datasets are unlikely to page until volume grows. Disable
 * buttons when at boundaries.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPrevious: () => void
  onNext: () => void
  disabled?: boolean
}

export function Pagination({
  currentPage,
  totalPages,
  onPrevious,
  onNext,
  disabled = false,
}: PaginationProps): React.JSX.Element {
  const atStart = currentPage <= 1
  const atEnd = currentPage >= totalPages

  return (
    <nav
      aria-label="Pagination"
      className="flex items-center justify-between gap-3 text-sm text-muted-foreground"
    >
      <span aria-live="polite">
        Page {currentPage} of {Math.max(totalPages, 1)}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrevious}
          disabled={disabled || atStart}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={disabled || atEnd}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  )
}
